// Ship classes, ammunition, captain personalities and difficulty levels.
// Every class is built on the same hull lines (data/ship.ts), scaled per axis,
// with its own rig, guns, crew and temperament.

import { CANNONS, SAIL_PLANS, type MastId, type SailPlan } from './ship';

export type ShipClassId = 'raider' | 'schooner' | 'merchant' | 'frigate' | 'warship';
export type AmmoId = 'round' | 'chain' | 'grape' | 'shell';
export type PersonalityId = 'reckless' | 'cunning' | 'timid' | 'disciplined' | 'methodical' | 'skirmisher';
export type DifficultyId = 'easy' | 'normal' | 'hard' | 'legendary';

export interface ShipClass {
  id: ShipClassId;
  name: string;
  role: string;
  /** Scale of the design hull: length (x), depth / height (y), beam (z). */
  scale: { x: number; y: number; z: number };
  masts: readonly MastId[];
  sails: readonly string[];
  plans: readonly SailPlan[];
  /** Indices into CANNONS that this class carries. */
  guns: readonly number[];
  /** Shot weight relative to a 12-pounder. */
  gunWeight: number;
  /** Full complement. */
  crew: number;
  /** How many of them are simulated individually on deck. */
  deckCrew: number;
  /** Plank thickness / frame strength relative to the frigate. */
  hull: number;
  ammo: readonly AmmoId[];
  personality: PersonalityId;
  /** Physics column spacing (design metres); coarser is cheaper. */
  columns: { dx: number; dz: number };
  /** Ballast: centre of gravity height (design metres). */
  cgY: number;
  /** Paint: main topside colour and strake colour (linear RGB). */
  paint: { hull: [number, number, number]; strake: [number, number, number] };
  /** Cargo value (merchants), used by the AI to decide what's worth taking. */
  cargo: number;
  /** Sail power relative to rig area (cut and set of the canvas). */
  sailArea: number;
}

const gunsAt = (xs: number[]) => CANNONS.map((c, i) => (xs.includes(c.x) ? i : -1)).filter((i) => i >= 0);
const allGuns = CANNONS.map((_, i) => i);

const SCHOONER_PLANS: SailPlan[] = [
  { name: 'Furled', deploy: {} },
  { name: 'Storm canvas', deploy: { fore_staysail: 1, main_gaff: 0.5 } },
  { name: 'Fighting sail', deploy: { fore_staysail: 1, jib: 1, fore_gaff: 1, main_gaff: 1 } },
  { name: 'Plain sail', deploy: { fore_staysail: 1, jib: 1, fore_gaff: 1, main_gaff: 1, fore_topsail: 1 } },
  { name: 'All plain sail', deploy: { fore_staysail: 1, jib: 1, flying_jib: 1, fore_gaff: 1, main_gaff: 1, fore_topsail: 1 } },
];

export const SHIP_CLASSES: Record<ShipClassId, ShipClass> = {
  raider: {
    id: 'raider', name: 'Brigantine raider', role: 'Fast attack and boarding',
    scale: { x: 0.56, y: 0.62, z: 0.6 },
    masts: ['fore', 'main'],
    sails: ['fore_course', 'fore_topsail', 'main_course', 'main_topsail', 'jib', 'fore_staysail'],
    plans: SAIL_PLANS, guns: gunsAt([-2.2, 0.0, 3.4, 6.2]), gunWeight: 0.5, crew: 60, deckCrew: 26, hull: 0.55,
    ammo: ['round', 'grape', 'chain'], personality: 'skirmisher', columns: { dx: 2.4, dz: 1.6 }, cgY: -1.2,
    paint: { hull: [0.05, 0.04, 0.035], strake: [0.3, 0.02, 0.02] }, cargo: 0.2, sailArea: 1.8,
  },
  schooner: {
    id: 'schooner', name: 'Topsail schooner', role: 'Weatherly harrier, cuts rigging',
    scale: { x: 0.62, y: 0.62, z: 0.52 },
    masts: ['fore', 'main'],
    sails: ['fore_gaff', 'main_gaff', 'jib', 'flying_jib', 'fore_staysail', 'fore_topsail'],
    plans: SCHOONER_PLANS, guns: gunsAt([-2.2, 3.4, 6.2]), gunWeight: 0.5, crew: 48, deckCrew: 22, hull: 0.5,
    ammo: ['round', 'chain', 'grape'], personality: 'cunning', columns: { dx: 2.4, dz: 1.6 }, cgY: -1.3,
    paint: { hull: [0.12, 0.13, 0.1], strake: [0.55, 0.45, 0.2] }, cargo: 0.3, sailArea: 1.6,
  },
  merchant: {
    id: 'merchant', name: 'Merchantman', role: 'Laden trader; runs rather than fights',
    scale: { x: 0.86, y: 0.92, z: 1.08 },
    masts: ['fore', 'main', 'mizzen'],
    sails: ['fore_course', 'fore_topsail', 'main_course', 'main_topsail', 'mizzen_topsail', 'jib', 'fore_staysail', 'spanker'],
    plans: SAIL_PLANS, guns: gunsAt([-2.2, 3.4]), gunWeight: 0.5, crew: 28, deckCrew: 18, hull: 0.8,
    ammo: ['round'], personality: 'timid', columns: { dx: 2.2, dz: 1.5 }, cgY: -0.9,
    paint: { hull: [0.2, 0.14, 0.08], strake: [0.2, 0.25, 0.3] }, cargo: 1, sailArea: 0.85,
  },
  frigate: {
    id: 'frigate', name: 'Frigate', role: 'Balanced man-of-war',
    scale: { x: 1, y: 1, z: 1 },
    masts: ['fore', 'main', 'mizzen'],
    sails: ['fore_course', 'fore_topsail', 'fore_topgallant', 'main_course', 'main_topsail', 'main_topgallant', 'mizzen_topsail', 'spanker', 'fore_staysail', 'jib', 'flying_jib'],
    plans: SAIL_PLANS, guns: allGuns, gunWeight: 1, crew: 160, deckCrew: 46, hull: 1,
    ammo: ['round', 'chain', 'grape', 'shell'], personality: 'disciplined', columns: { dx: 1.5, dz: 1.0 }, cgY: -1.1,
    paint: { hull: [0.035, 0.033, 0.03], strake: [0.36, 0.13, 0.06] }, cargo: 0.4, sailArea: 1,
  },
  warship: {
    id: 'warship', name: 'Heavy warship', role: 'Slow, massive broadsides, hard to sink',
    scale: { x: 1.32, y: 1.28, z: 1.3 },
    masts: ['fore', 'main', 'mizzen'],
    sails: ['fore_course', 'fore_topsail', 'fore_topgallant', 'main_course', 'main_topsail', 'main_topgallant', 'mizzen_topsail', 'spanker', 'fore_staysail', 'jib', 'flying_jib'],
    plans: SAIL_PLANS, guns: allGuns, gunWeight: 2, crew: 420, deckCrew: 60, hull: 2.3,
    ammo: ['round', 'chain', 'grape', 'shell'], personality: 'methodical', columns: { dx: 2.0, dz: 1.4 }, cgY: -1.4,
    paint: { hull: [0.06, 0.05, 0.03], strake: [0.55, 0.42, 0.12] }, cargo: 0.5, sailArea: 0.62,
  },
};

export interface Ammo {
  id: AmmoId;
  name: string;
  /** Mass of one projectile for a 12-pounder, kg. */
  mass: number;
  /** Muzzle velocity, m/s. */
  speed: number;
  /** Quadratic drag factor 0.5*rho*Cd*A/m, 1/m. */
  drag: number;
  /** Projectiles per gun. */
  count: number;
  /** Cone half-angle for multi-ball loads, rad. */
  spread: number;
  /** Damage multipliers. */
  hull: number;
  sail: number;
  rig: number;
  crew: number;
  /** Plank penetration relative to round shot. */
  penetration: number;
  /** Reload time multiplier. */
  reload: number;
  /** Effective range, m. */
  range: number;
  explosive: boolean;
}

export const AMMO: Record<AmmoId, Ammo> = {
  round: { id: 'round', name: 'Round shot', mass: 5.4, speed: 390, drag: 0.00062, count: 1, spread: 0, hull: 1, sail: 0.35, rig: 0.6, crew: 0.7, penetration: 1, reload: 1, range: 1300, explosive: false },
  chain: { id: 'chain', name: 'Chain shot', mass: 5.2, speed: 300, drag: 0.0021, count: 1, spread: 0, hull: 0.25, sail: 3.2, rig: 2.6, crew: 0.8, penetration: 0.3, reload: 1.15, range: 550, explosive: false },
  grape: { id: 'grape', name: 'Grapeshot', mass: 0.45, speed: 330, drag: 0.0042, count: 9, spread: 0.035, hull: 0.08, sail: 0.3, rig: 0.35, crew: 1.6, penetration: 0.1, reload: 1, range: 340, explosive: false },
  shell: { id: 'shell', name: 'Explosive shell', mass: 4.2, speed: 290, drag: 0.0008, count: 1, spread: 0, hull: 0.7, sail: 0.5, rig: 0.8, crew: 1.2, penetration: 0.5, reload: 1.7, range: 1000, explosive: true },
};

export interface Personality {
  id: PersonalityId;
  /** Willingness to close and accept damage, 0..1. */
  aggression: number;
  /** Preferred engagement range, m. */
  range: number;
  /** Hull integrity below which she breaks off, 0..1. */
  retreatHull: number;
  /** Crew fraction below which she breaks off. */
  retreatCrew: number;
  boarding: number;
  ramming: number;
  /** Prefers to cripple rigging before closing. */
  cripple: boolean;
  /** Fires disciplined broadsides rather than as guns bear. */
  broadsides: number;
  /** Flees merchants' style on first sight of a stronger ship. */
  runsFromStronger: boolean;
  /** Morale below which her people give up a fight they can't win (pirates, with the gallows waiting, hold on longest). */
  strikeMorale: number;
  /**
   * Hit and run: against a stronger ship she doesn't run for good but
   * harries - in across a bow or stern to rake when her guns are loaded,
   * off out of reach while they're reloaded, and back again.
   */
  hitAndRun: boolean;
  /** How much she wants the enemy on her own beam (her broadside bearing), and how much she shies from the enemy's. */
  beam: number;
  wary: number;
}

export const PERSONALITIES: Record<PersonalityId, Personality> = {
  reckless: { id: 'reckless', aggression: 0.9, range: 140, retreatHull: 0.18, retreatCrew: 0.3, boarding: 0.85, ramming: 0.55, cripple: false, broadsides: 0.3, runsFromStronger: false, strikeMorale: 0.12, hitAndRun: false, beam: 1, wary: 0.6 },
  cunning: { id: 'cunning', aggression: 0.5, range: 420, retreatHull: 0.42, retreatCrew: 0.45, boarding: 0.35, ramming: 0.1, cripple: true, broadsides: 0.5, runsFromStronger: false, strikeMorale: 0.24, hitAndRun: false, beam: 1, wary: 1.2 },
  timid: { id: 'timid', aggression: 0.05, range: 700, retreatHull: 0.95, retreatCrew: 0.9, boarding: 0, ramming: 0, cripple: false, broadsides: 0.2, runsFromStronger: true, strikeMorale: 0.55, hitAndRun: false, beam: 0.5, wary: 1 },
  disciplined: { id: 'disciplined', aggression: 0.6, range: 300, retreatHull: 0.3, retreatCrew: 0.4, boarding: 0.4, ramming: 0.15, cripple: false, broadsides: 0.8, runsFromStronger: false, strikeMorale: 0.22, hitAndRun: false, beam: 1.2, wary: 1 },
  methodical: { id: 'methodical', aggression: 0.45, range: 420, retreatHull: 0.22, retreatCrew: 0.3, boarding: 0.2, ramming: 0.25, cripple: false, broadsides: 1, runsFromStronger: false, strikeMorale: 0.18, hitAndRun: false, beam: 2.2, wary: 0.35 },
  /** The raider's way: fast in to rake, away to reload, never broadside to broadside with a heavier ship. */
  skirmisher: { id: 'skirmisher', aggression: 0.75, range: 170, retreatHull: 0.25, retreatCrew: 0.35, boarding: 0.7, ramming: 0.15, cripple: false, broadsides: 0.6, runsFromStronger: false, strikeMorale: 0.12, hitAndRun: true, beam: 0.8, wary: 2.2 },
};

export interface Difficulty {
  id: DifficultyId;
  name: string;
  /** Seconds between tactical re-evaluations. */
  decisionInterval: number;
  /** Delay before reacting to new information, s. */
  reaction: number;
  /** Candidate headings considered per decision. */
  headings: number;
  /** Look-ahead used when scoring manoeuvres, s. */
  horizon: number;
  /** Noise added to decision scores (imperfect judgement). */
  noise: number;
  /** How well ships share contacts and split up around a target, 0..1. */
  coordination: number;
  /** Seconds for a sighting to be signalled to consorts. */
  signalDelay: number;
  /** Chooses weak sections, focus fire and raking positions. */
  tactics: number;
  /** Small nudge to crew experience (not a raw accuracy bonus). */
  crewSkill: number;
}

export const DIFFICULTIES: Record<DifficultyId, Difficulty> = {
  easy: { id: 'easy', name: 'Easy', decisionInterval: 2.6, reaction: 3.5, headings: 8, horizon: 10, noise: 0.45, coordination: 0, signalDelay: 45, tactics: 0.1, crewSkill: -0.1 },
  normal: { id: 'normal', name: 'Normal', decisionInterval: 1.4, reaction: 1.8, headings: 16, horizon: 18, noise: 0.22, coordination: 0.5, signalDelay: 22, tactics: 0.5, crewSkill: 0 },
  hard: { id: 'hard', name: 'Hard', decisionInterval: 0.8, reaction: 0.9, headings: 24, horizon: 26, noise: 0.1, coordination: 0.8, signalDelay: 10, tactics: 0.8, crewSkill: 0.04 },
  legendary: { id: 'legendary', name: 'Legendary', decisionInterval: 0.45, reaction: 0.5, headings: 36, horizon: 34, noise: 0.05, coordination: 1, signalDelay: 5, tactics: 1, crewSkill: 0.08 },
};
